import { useState, useEffect, useCallback } from 'react';

import Cookies from 'js-cookie';

import { EnvironmentUtils } from '@anyx/shared-utils';


import { AI_AUTO_REPLY_API_HOST } from '../api';

interface UsePromptsClientQueryReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePromptsClientQuery = <T>(
  endpoint: string,
  enabled = true
): UsePromptsClientQueryReturn<T> => {
  const host = AI_AUTO_REPLY_API_HOST[EnvironmentUtils.getCurrentEnvironment()];

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);

      const token = Cookies.get('authorization');

      const response = await fetch(`${host}${endpoint}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [host, endpoint, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = () => {
    fetchData();
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
};
